/**
 * useComparisonState Hook
 *
 * Manages comparison UI state including model selection and active criterion.
 * Handles URL synchronization for deep linking and state restoration.
 *
 * Part of Consensus Heatmap Comparison feature (behind newDesign flag)
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Criterion } from '../types';

export interface ComparisonState {
  selectedModels: string[]; // Max 2 model keys
  activeCriterion: Criterion | null;
  canCompare: boolean; // True when exactly 2 models selected
}

export interface UseComparisonStateReturn extends ComparisonState {
  selectModel: (modelKey: string) => void;
  deselectModel: (modelKey: string) => void;
  toggleModel: (modelKey: string) => void;
  setActiveCriterion: (criterion: Criterion | null) => void;
  clearSelection: () => void;
}

/**
 * Custom hook for managing comparison state
 *
 * Future backend integration:
 * - URL params: ?models=modelA,modelB&criterion=factual_accuracy
 * - History state: { scrollY, previousCriterion }
 * - API call on criterion change: GET /api/consensus/:id/lens?criterion=X&models=A,B
 */
export const useComparisonState = (): UseComparisonStateReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [activeCriterion, setActiveCriterionState] = useState<Criterion | null>(null);

  // Parse URL params on mount (for deep linking)
  useEffect(() => {
    const modelsParam = searchParams.get('models');
    const criterionParam = searchParams.get('criterion');

    if (modelsParam) {
      const models = modelsParam.split(',').filter(Boolean).slice(0, 2); // Max 2
      setSelectedModels(models);
    }

    if (criterionParam) {
      setActiveCriterionState(criterionParam as Criterion);
    }
  }, []); // Only on mount

  // Update URL when state changes
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    if (selectedModels.length > 0) {
      newParams.set('models', selectedModels.join(','));
    } else {
      newParams.delete('models');
    }

    if (activeCriterion) {
      newParams.set('criterion', activeCriterion);
    } else {
      newParams.delete('criterion');
    }

    // Only update if params actually changed
    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedModels, activeCriterion]);

  const selectModel = (modelKey: string) => {
    setSelectedModels(prev => {
      if (prev.includes(modelKey)) {
        return prev; // Already selected
      }
      if (prev.length < 2) {
        return [...prev, modelKey];
      }
      // Replace oldest selection if already at max
      return [prev[1], modelKey];
    });
  };

  const deselectModel = (modelKey: string) => {
    setSelectedModels(prev => prev.filter(m => m !== modelKey));
  };

  const toggleModel = (modelKey: string) => {
    if (selectedModels.includes(modelKey)) {
      deselectModel(modelKey);
    } else {
      selectModel(modelKey);
    }
  };

  const setActiveCriterion = (criterion: Criterion | null) => {
    setActiveCriterionState(criterion);

    // TODO: Backend integration - fetch lens data when criterion changes
    // if (criterion && selectedModels.length === 2) {
    //   fetchLensData(comparisonId, criterion, selectedModels);
    // }
  };

  const clearSelection = () => {
    setSelectedModels([]);
    setActiveCriterionState(null);
  };

  const canCompare = selectedModels.length === 2;

  return {
    selectedModels,
    activeCriterion,
    canCompare,
    selectModel,
    deselectModel,
    toggleModel,
    setActiveCriterion,
    clearSelection,
  };
};
